import { jsx as _jsx } from "react/jsx-runtime";
import {} from 'react';
import { cn } from "../lib/cn.js";
/**
 * O avatar de um contato — iniciais sobre uma cor estável (F083).
 *
 * ## Por que iniciais, e não a foto
 *
 * Não é escolha estética: **a Cloud API do WhatsApp não expõe a foto de perfil
 * de um contato.** O que o gateway guarda em `contacts_addresses.extra` é
 * `{name, username, phone_number, bsuid, address_type}` — só o Instagram tem
 * `profile_picture_url`. Iniciais é o teto do que existe, e dizer isso aqui
 * evita que a próxima pessoa procure a foto por meia hora.
 *
 * ## Por que a cor é derivada, e não sorteada
 *
 * Numa lista de cem conversas a cor é o que o olho usa para reencontrar
 * alguém. Se ela mudasse a cada renderização — ou entre a lista e o cabeçalho
 * da conversa — seria ruído em vez de informação. Derivar do identificador
 * garante que o mesmo contato tem sempre a mesma cor, em qualquer tela, em
 * qualquer sessão, sem guardar nada.
 *
 * As seis cores são tokens (`--avatar-1`…`--avatar-6`), com variante de tema
 * escuro: os tons do tema claro não têm contraste suficiente sobre superfície
 * escura.
 *
 * ## Acessibilidade
 *
 * O avatar é DECORATIVO. O nome do contato sempre aparece ao lado dele, em
 * texto de verdade — então anunciá-lo de novo seria repetir. Daí
 * `aria-hidden`: um leitor de tela que lesse "T, Thiago Leblanc" estaria
 * gastando o tempo de quem ouve.
 */
const PALETTE_SIZE = 6;
/**
 * FNV-1a de 32 bits — pequeno, estável e sem dependência.
 *
 * O requisito é distribuir, não resistir a ataque: a semente é um número de
 * telefone e o resultado escolhe entre seis cores. Um hash criptográfico aqui
 * seria custo sem ganho.
 */
const hashSeed = (seed) => {
    let hash = 0x811c9dc5;
    for (let i = 0; i < seed.length; i += 1) {
        hash ^= seed.charCodeAt(i);
        hash = Math.imul(hash, 0x01000193);
    }
    return Math.abs(hash);
};
/**
 * Até duas iniciais, do que a pessoa reconheceria.
 *
 * "Thiago Leblanc" → TL. "Thiago" → T. Um número puro ("554298543579") não
 * tem inicial nenhuma que signifique algo, então devolve vazio e o componente
 * mostra o ponto neutro — melhor que "5" ou "55", que não distinguem ninguém
 * num país inteiro com o mesmo prefixo.
 */
const initialsOf = (label) => {
    const words = label
        .trim()
        .split(/\s+/)
        .filter((word) => /\p{L}/u.test(word));
    if (words.length === 0)
        return '';
    const first = words[0]?.[0] ?? '';
    const last = words.length > 1 ? (words[words.length - 1]?.[0] ?? '') : '';
    return (first + last).toUpperCase();
};
const SIZE_CLASS = {
    sm: 'h-8 w-8 text-(length:--text-xs)',
    md: 'h-10 w-10 text-(length:--text-sm)',
};
export const Avatar = ({ label, seed, size = 'sm', className, }) => {
    const initials = initialsOf(label);
    const index = (hashSeed(seed) % PALETTE_SIZE) + 1;
    return (_jsx("span", { "aria-hidden": "true", className: cn('inline-flex shrink-0 select-none items-center justify-center', 'rounded-(--radius-full) font-(--weight-semibold) leading-none', SIZE_CLASS[size], className), style: {
            backgroundColor: `var(--avatar-${String(index)})`,
            color: 'var(--avatar-fg)',
        }, children: initials === '' ? (_jsx("span", { className: "h-1.5 w-1.5 rounded-(--radius-full) bg-current opacity-70" })) : (initials) }));
};
//# sourceMappingURL=avatar.js.map